// The 2-second hold (SPEC 5.4). Nothing leaves the phone until the hold completes, so Cancel is
// certain. Hidden page, lock screen, lost connection or a frozen timer all cancel; the answer is
// never fired late.
import { HOLD_MS } from "@whistle/protocol";

export type HoldCancelReason = "user" | "hidden" | "disconnected" | "late" | "changed";

export type HoldOptions = {
  ms?: number;
  /** How far past the deadline a tick may arrive before we decide the page was frozen and cancel. */
  lateToleranceMs?: number;
  tickMs?: number;
  onTick?: (remainingMs: number) => void;
  onFire: () => void;
  onCancel: (reason: HoldCancelReason) => void;
  isConnected?: () => boolean;
  doc?: Pick<Document, "hidden" | "addEventListener" | "removeEventListener">;
  /** pagehide fires on the window, not the document. */
  win?: Pick<Window, "addEventListener" | "removeEventListener">;
  now?: () => number;
};

export type HoldHandle = { cancel: (reason?: HoldCancelReason) => void; readonly done: boolean };

export function startHold(opts: HoldOptions): HoldHandle {
  const ms = opts.ms ?? HOLD_MS;
  const tolerance = opts.lateToleranceMs ?? 750;
  const tickMs = opts.tickMs ?? 50;
  const now = opts.now ?? (() => Date.now());
  const doc = opts.doc ?? (typeof document !== "undefined" ? document : undefined);
  const win = opts.win ?? (typeof window !== "undefined" ? window : undefined);
  const start = now();
  let done = false;
  let timer: ReturnType<typeof setInterval> | undefined;

  const finish = () => {
    done = true;
    if (timer !== undefined) clearInterval(timer);
    doc?.removeEventListener("visibilitychange", onVisibility);
    win?.removeEventListener("pagehide", onHide);
    doc?.removeEventListener("freeze", onHide);
  };
  const cancel = (reason: HoldCancelReason = "user") => {
    if (done) return;
    finish();
    opts.onCancel(reason);
  };
  const onVisibility = () => {
    if (doc?.hidden) cancel("hidden");
  };
  const onHide = () => cancel("hidden");

  const tick = () => {
    if (done) return;
    if (doc?.hidden) return cancel("hidden");
    if (opts.isConnected && !opts.isConnected()) return cancel("disconnected");
    const elapsed = now() - start;
    if (elapsed >= ms + tolerance) return cancel("late");
    if (elapsed >= ms) {
      finish();
      opts.onFire();
      return;
    }
    opts.onTick?.(ms - elapsed);
  };

  if (doc?.hidden) {
    // Started while hidden: never arm.
    queueMicrotask(() => cancel("hidden"));
    return { cancel, get done() { return done; } };
  }
  doc?.addEventListener("visibilitychange", onVisibility);
  win?.addEventListener("pagehide", onHide);
  doc?.addEventListener("freeze", onHide);
  opts.onTick?.(ms);
  timer = setInterval(tick, tickMs);
  return { cancel, get done() { return done; } };
}

/** "2 s", "1 s" for the banner: whole seconds, rounded up so it never shows 0 before firing. */
export function holdSeconds(remainingMs: number): number {
  return Math.max(1, Math.ceil(remainingMs / 1000));
}

export function cancelText(reason: HoldCancelReason): string {
  switch (reason) {
    case "user":
      return "Not sent. You cancelled.";
    case "hidden":
      return "Not sent. The app was closed during the 2-second wait.";
    case "disconnected":
      return "Not sent. The app lost its connection.";
    case "late":
      return "Not sent. The phone paused the app during the 2-second wait.";
    case "changed":
      return "Not sent. The question changed.";
  }
}
